import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { fetchVehicleServiceOrders, downloadServiceOrderPdf } from "@/lib/api";
import { ClockCounterClockwise, FilePdf, Wrench, Package } from "@phosphor-icons/react";
import { toast } from "sonner";

const statusColor = {
  aberta: "bg-blue-100 text-blue-700 border-blue-200",
  em_andamento: "bg-amber-100 text-amber-700 border-amber-200",
  finalizada: "bg-emerald-100 text-emerald-700 border-emerald-200",
  cancelada: "bg-gray-100 text-gray-600 border-gray-200",
};

const statusLabel = {
  aberta: "Aberta",
  em_andamento: "Em andamento",
  finalizada: "Finalizada",
  cancelada: "Cancelada",
};

const fmtMoney = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso; }
};

const sumApproved = (items = []) =>
  items.filter((i) => i.aprovado).reduce((s, i) => s + (i.quantidade * i.preco_unitario), 0);

export default function VehicleHistoryDialog({ vehicle, open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !vehicle?.id) return;
    setLoading(true);
    fetchVehicleServiceOrders(vehicle.id)
      .then(setItems)
      .catch(() => toast.error("Erro ao carregar histórico"))
      .finally(() => setLoading(false));
  }, [open, vehicle?.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] max-h-[85vh] overflow-hidden flex flex-col" data-testid="vehicle-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ClockCounterClockwise size={20} weight="duotone" className="text-[#00B0F0]" />
            Histórico do Veículo
          </DialogTitle>
          {vehicle && (
            <DialogDescription className="text-sm text-[#475569]">
              <span className="font-mono font-semibold text-[#001E50]">{vehicle.placa}</span>
              {" • "}
              <span>{vehicle.modelo}</span>
              {vehicle.ano ? ` • ${vehicle.ano}` : ""}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1">
          {loading && <p className="text-sm text-[#475569] py-4">Carregando...</p>}
          {!loading && items.length === 0 && (
            <div className="text-center py-10 text-[#475569] text-sm" data-testid="history-empty">
              Nenhuma OS registrada para este veículo ainda.
            </div>
          )}

          <ol className="relative border-l-2 border-[#E2E8F0] ml-3 space-y-5">
            {items.map((os) => {
              const total = sumApproved(os.servicos) + sumApproved(os.pecas) - (os.desconto || 0);
              return (
                <li key={os.id} className="pl-5 relative" data-testid={`history-item-${os.id}`}>
                  <span className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-[#00B0F0] border-2 border-white shadow" />
                  <div className="bg-white border border-[#E2E8F0] rounded-md p-4 hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-lg font-semibold text-[#001E50]">
                            OS #{String(os.numero).padStart(4, "0")}
                          </span>
                          <Badge variant="outline" className={statusColor[os.status]}>
                            {statusLabel[os.status]}
                          </Badge>
                        </div>
                        <p className="text-xs text-[#475569] mt-1">
                          Abertura: {fmtDate(os.data_abertura)}
                          {os.data_finalizacao && ` • Finalizada: ${fmtDate(os.data_finalizacao)}`}
                          {os.km_atual ? ` • ${Number(os.km_atual).toLocaleString("pt-BR")} km` : ""}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569]">Total Aprov.</div>
                        <div className="font-semibold tabular-nums text-[#001E50]">{fmtMoney(Math.max(total, 0))}</div>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                      <div className="flex items-center gap-1.5 text-[#334155]">
                        <Wrench size={14} className="text-[#001E50]" />
                        {(os.servicos || []).length} serviço(s)
                      </div>
                      <div className="flex items-center gap-1.5 text-[#334155]">
                        <Package size={14} className="text-[#001E50]" />
                        {(os.pecas || []).length} peça(s)
                      </div>
                    </div>

                    {os.observacoes && (
                      <p className="mt-2 text-xs text-[#475569] italic border-l-2 border-[#E2E8F0] pl-2">
                        {os.observacoes}
                      </p>
                    )}

                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await downloadServiceOrderPdf(os.id, os.numero);
                          } catch (e) {
                            toast.error("Erro ao baixar PDF");
                          }
                        }}
                        className="inline-flex items-center gap-1 text-xs text-[#001E50] hover:text-[#00B0F0] font-medium"
                        data-testid={`history-pdf-${os.id}`}
                      >
                        <FilePdf size={14} /> Baixar PDF
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </DialogContent>
    </Dialog>
  );
}
